import { useEffect, useState, useCallback } from "react";
import { Target, Loader2, AlertCircle } from "lucide-react";
import * as admin from "@/api/admin";
import type { ShelfGaps, OutreachRow } from "@/api/admin";
import { MetroSelect } from "@/components/MetroSelect";

// ═══════════════════════════════════════════════════════════════════
// Recruitment — who to call, and which shelf to go fill.
//
// Both reports on one page because they are two halves of one question and
// reading them apart is how you conclude the wrong thing. The worklist ranks
// LISTINGS, so it is silent about a trade with none; the shelf-gap report is
// the only surface that can speak about an empty shelf.
//
// ADMIN ONLY. These are our notes about approaching a business, never facts
// about it, and nothing here may be folded into a public read.
// ═══════════════════════════════════════════════════════════════════

export default function Recruitment() {
  const [metro, setMetro] = useState("south_bay");
  const [gaps, setGaps] = useState<ShelfGaps | null>(null);
  const [rows, setRows] = useState<OutreachRow[]>([]);
  const [totals, setTotals] = useState<{ unclaimed: number; contactable: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      const [g, o] = await Promise.all([
        admin.getShelfGaps({ metro: code, days: 30 }),
        admin.getOutreachWorklist({ limit: 25 }),
      ]);
      setGaps(g.data);
      setRows(o.data.rows);
      setTotals(o.data.totals);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(metro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <Target className="w-6 h-6 text-orange-600" /> Recruitment
        </h1>
        <p className="text-sm text-gray-600">
          A shopping trip that finds nothing leaves no voice, no question and no order — it is
          invisible in every other table. <strong>These are the only places it is written down.</strong>
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <MetroSelect value={metro} onChange={setMetro} />
        </div>
        <button
          onClick={() => void load(metro.trim())}
          className="bg-gray-900 text-white text-sm rounded px-4 py-1.5"
        >
          Load
        </button>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* ─── Shelf gaps ─────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="font-semibold">Shelves — demand we cannot serve</h2>
        <p className="text-sm text-gray-600">
          Sorted by unmet demand, then demand, then emptiness: a shelf people walk away from
          outranks a bare shelf nobody has asked for.
          {gaps && gaps.unshelvedShoppers > 0 && (
            <>
              {" "}
              <strong>{gaps.unshelvedShoppers}</strong> shopping trip
              {gaps.unshelvedShoppers === 1 ? "" : "s"} could not be filed under any shelf at all —
              possibly a category worth creating.
            </>
          )}
        </p>

        {/* ── what the search box saw, which this page was blind to ──────── */}
        {gaps && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm space-y-2">
            <p>
              <strong>From the search box.</strong> Everything above comes from the
              community composer — somebody writing a sentence. Most people just
              search, and until now none of that reached this page.
            </p>
            <p className="text-gray-700">
              <strong>{gaps.searchesWithNothing.inThisMetro}</strong> search
              {gaps.searchesWithNothing.inThisMetro === 1 ? "" : "es"} from this metro came
              back completely empty, plus{" "}
              <strong>{gaps.searchesWithNothing.locationNotGiven}</strong> more that did not
              say where the person was. Those two are kept apart on purpose — a caller only
              sends a location when they choose to narrow, so adding them would count demand
              from anywhere as demand from here.
            </p>
            {gaps.wordsNobodyHasWritten.length > 0 ? (
              <div>
                <p className="text-gray-700 mb-1">
                  Words people searched for that appear in <strong>no content at all</strong>.
                  Each is a trade or a product nobody offers — this is the recruitment list.
                  Read the top, not the tail: a real gap is asked for again and again, noise
                  turns up once.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {gaps.wordsNobodyHasWritten.map((w) => (
                    <span
                      key={w.term}
                      className="text-xs px-2 py-0.5 rounded border border-amber-400 bg-white"
                    >
                      {w.term} <span className="text-gray-500">×{w.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-600">
                Every word people searched for exists somewhere in the content.
              </p>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 rounded bg-white">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left p-2">Shelf</th>
                <th className="text-right p-2">Shoppers</th>
                <th className="text-right p-2">Found nothing</th>
                <th className="text-right p-2">Businesses</th>
                <th className="text-right p-2">Claimed</th>
                <th className="text-right p-2">Stalls</th>
              </tr>
            </thead>
            <tbody>
              {gaps?.shelves.map((s) => (
                <tr key={s.categoryCode} className="border-t border-gray-100 align-top">
                  <td className="p-2">
                    <div className="font-medium">{s.name}</div>
                    {s.pitch && <div className="text-xs text-gray-600 mt-0.5">{s.pitch}</div>}
                  </td>
                  <td className="p-2 text-right tabular-nums">{s.shoppers}</td>
                  <td
                    className={`p-2 text-right tabular-nums ${
                      s.foundNothing > 0 ? "text-red-700 font-semibold" : "text-gray-400"
                    }`}
                  >
                    {s.foundNothing}
                  </td>
                  <td
                    className={`p-2 text-right tabular-nums ${
                      s.businesses === 0 ? "text-red-700 font-semibold" : ""
                    }`}
                  >
                    {s.businesses}
                  </td>
                  <td
                    className={`p-2 text-right tabular-nums ${
                      s.claimed === 0 ? "text-amber-700" : ""
                    }`}
                  >
                    {s.claimed}
                  </td>
                  <td className="p-2 text-right tabular-nums text-gray-500">{s.stalls}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500">
          Demand columns read zero until the composer has run — they are recent. An empty
          <strong> pitch</strong> is deliberate: no evidence, no sentence.
        </p>
      </section>

      {/* ─── Outreach worklist ──────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="font-semibold">Listings — who to call</h2>
        <p className="text-sm text-gray-600">
          Unclaimed listings ranked by the demand already waiting for them. An unanswered question
          and a turned-away shopper count <strong>equally</strong> — both mean somebody asked us and
          got silence.
          {totals && (
            <>
              {" "}
              {totals.unclaimed} unclaimed, {totals.contactable} reachable.
            </>
          )}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 rounded bg-white">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left p-2">Business</th>
                <th className="text-left p-2">Trade</th>
                <th className="text-right p-2">Asks</th>
                <th className="text-right p-2">Shoppers</th>
                <th className="text-right p-2">Vouches</th>
                <th className="text-right p-2">Score</th>
                <th className="text-left p-2">Reach</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.providerId} className="border-t border-gray-100">
                  <td className="p-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-gray-500">
                      {r.city}, {r.state} · {r.outreachStatus}
                    </div>
                  </td>
                  <td className="p-2 text-gray-600">{r.categoryCode}</td>
                  <td className="p-2 text-right tabular-nums">{r.waitingAsks}</td>
                  <td className="p-2 text-right tabular-nums">{r.waitingShoppers}</td>
                  <td className="p-2 text-right tabular-nums text-gray-500">{r.vouchCount}</td>
                  <td className="p-2 text-right tabular-nums font-medium">{r.score}</td>
                  <td className="p-2 text-xs text-gray-600">
                    {r.contact.email ?? r.contact.phone ?? r.contact.website ?? (
                      <span className="text-amber-700">research needed</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-gray-500">
                    Nothing unclaimed to call.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
